import { Anchor, List, Paragraph, Text } from '@zillow/constellation-10';

import { RentalManagerUserPropertyRecommendationsWebApiHandler_recommendations_Path } from '@zg-rentals/ts-rental-manager-api-beta-client';

const GET_LISTING_RECS = RentalManagerUserPropertyRecommendationsWebApiHandler_recommendations_Path;

const RECS = {
  AMENITIES: 'amenities',
  CONTACT: 'contact',
  APPLICATIONS: 'applications',
  DATE_AVAILABLE: 'dateAvailable',
  DESCRIPTION: 'description',
  HIDE_PHONE: 'hidePhone',
  HIDE_STREET_ADDRESS: 'hideStreetAddress',
  LEASE_TERMS: 'leaseTerms',
  PETS_NOT_ALLOWED: 'petsNotAllowed',
  PHOTOS: 'photos',
  // ROOM_DESCRIPTION not an actual option from api but need to accomodate r4r learn more
  ROOM_DESCRIPTION: 'roomAndSharedSpaceDescription',
  // USED by PostingPathTourScheduling
  SCHEDULE: 'schedule',
  SECURITY_DEPOSIT: 'securityDeposit',
  SQUARE_FOOTAGE: 'squareFootage',
  VIRTUAL_TOUR: 'virtualTour',
  // MF SPECIFIC
  BUSINESS_HOURS: 'businessHours',
  FLOOR_PLAN_LAYOUT: 'floorPlanLayout',
  FLOOR_PLAN_PHOTOS: 'floorPlanPhotos',
  LAUNDRY: 'laundry',
  LEASE_LENGTH: 'leaseLength',
  NAME: 'name',
  PARKING: 'parking',
  PRICE: 'price',
  SPECIAL_OFFER: 'specialOffer',
  UNIT_AVAILABILITY: 'unitAvailability',
  WEBSITE: 'website',
  VIDEO: 'video',
  ONE_TIME_FEE: 'oneTimeFee',
  MONTHLY_FEE: 'monthlyFee',
};

const REVIEW_PAGE_SECTION_RECS = {
  AMENITIES: 'AMENITIES',
  RENT_DETAILS: 'RENT_DETAILS',
  // for SF listings this can also be the final details section
  CONTACT: 'CONTACT',
  MEDIA: 'MEDIA',
  PROPERTY_INFORMATION: 'PROPERTY_INFORMATION',
  SCREENING_CRITERIA: 'SCREENING_CRITERIA',
  // MF Specific
  BUILDING_DETAILS: 'BUILDING_DETAILS',
  COSTS_AND_FEES: 'COSTS_AND_FEES',
  FLOOR_PLANS: 'FLOOR_PLANS',
  KEY_FEATURES: 'KEY_FEATURES',
  OTHER_PROPERTY_FEATURES: 'OTHER_PROPERTY_FEATURES',
  POLICIES_AND_TERMS: 'POLICIES_AND_TERMS',
  SPECIAL_OFFERS: 'SPECIAL_OFFERS',
};

const COMPLETION_STATUS = {
  COMPLETE: 'complete',
  INCOMPLETE: 'incomplete',
};

const RECS_ARRAY_ORDER = {
  [REVIEW_PAGE_SECTION_RECS.BUILDING_DETAILS]: {
    [RECS.DESCRIPTION]: 0,
    [RECS.LEASE_TERMS]: 1,
    [RECS.WEBSITE]: 2,
    [RECS.BUSINESS_HOURS]: 3,
  },
  [REVIEW_PAGE_SECTION_RECS.POLICIES_AND_TERMS]: {
    [RECS.LEASE_LENGTH]: 0,
    [RECS.PETS_NOT_ALLOWED]: 1,
    [RECS.PARKING]: 2,
    [RECS.SECURITY_DEPOSIT]: 3,
  },
  [REVIEW_PAGE_SECTION_RECS.MEDIA]: {
    [RECS.PHOTOS]: 0,
    [RECS.VIRTUAL_TOUR]: 1,
    [RECS.VIDEO]: 2,
  },
};

const RECS_TO_MF_SECTION_MAP = {
  [RECS.DESCRIPTION]: REVIEW_PAGE_SECTION_RECS.BUILDING_DETAILS,
  [RECS.LEASE_TERMS]: REVIEW_PAGE_SECTION_RECS.BUILDING_DETAILS,
  [RECS.WEBSITE]: REVIEW_PAGE_SECTION_RECS.BUILDING_DETAILS,
  [RECS.BUSINESS_HOURS]: REVIEW_PAGE_SECTION_RECS.BUILDING_DETAILS,
  [RECS.PHOTOS]: REVIEW_PAGE_SECTION_RECS.MEDIA,
  [RECS.VIRTUAL_TOUR]: REVIEW_PAGE_SECTION_RECS.MEDIA,
  [RECS.VIDEO]: REVIEW_PAGE_SECTION_RECS.MEDIA,
  [RECS.PRICE]: REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS,
  [RECS.FLOOR_PLAN_LAYOUT]: REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS,
  [RECS.SQUARE_FOOTAGE]: REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS,
  [RECS.UNIT_AVAILABILITY]: REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS,
  [RECS.FLOOR_PLAN_PHOTOS]: REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS,
  [RECS.DATE_AVAILABLE]: REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS,
  [RECS.SPECIAL_OFFER]: REVIEW_PAGE_SECTION_RECS.SPECIAL_OFFERS,
  [RECS.AMENITIES]: REVIEW_PAGE_SECTION_RECS.OTHER_PROPERTY_FEATURES,
  [RECS.LAUNDRY]: REVIEW_PAGE_SECTION_RECS.KEY_FEATURES,
  [RECS.LEASE_LENGTH]: REVIEW_PAGE_SECTION_RECS.POLICIES_AND_TERMS,
  [RECS.PARKING]: REVIEW_PAGE_SECTION_RECS.POLICIES_AND_TERMS,
  [RECS.CONTACT]: REVIEW_PAGE_SECTION_RECS.CONTACT,
  [RECS.ONE_TIME_FEE]: REVIEW_PAGE_SECTION_RECS.COSTS_AND_FEES,
  [RECS.MONTHLY_FEE]: REVIEW_PAGE_SECTION_RECS.COSTS_AND_FEES,
  [RECS.PETS_NOT_ALLOWED]: REVIEW_PAGE_SECTION_RECS.SCREENING_CRITERIA,
};

const MF_SECTION_TO_INDEX_MAP = {
  [REVIEW_PAGE_SECTION_RECS.BUILDING_DETAILS]: 0,
  [REVIEW_PAGE_SECTION_RECS.CONTACT]: 1,
  [REVIEW_PAGE_SECTION_RECS.MEDIA]: 3,
  [REVIEW_PAGE_SECTION_RECS.FLOOR_PLANS]: 4,
  [REVIEW_PAGE_SECTION_RECS.SPECIAL_OFFERS]: 5,
  [REVIEW_PAGE_SECTION_RECS.COSTS_AND_FEES]: 6,
  [REVIEW_PAGE_SECTION_RECS.KEY_FEATURES]: 7,
  [REVIEW_PAGE_SECTION_RECS.OTHER_PROPERTY_FEATURES]: 9,
  [REVIEW_PAGE_SECTION_RECS.POLICIES_AND_TERMS]: 10,
  [REVIEW_PAGE_SECTION_RECS.SCREENING_CRITERIA]: 13,
};

const RecsLearnMoreConstants = {
  [RECS.AMENITIES]: {
    // eslint-disable-next-line constellation/numbers
    heading: 'Add at least 3 amenities',
    body: <Paragraph>Share which amenities are available at your property.</Paragraph>,
    premium: {
      heading: 'Add your amenities',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            Renters appreciate knowing whether your property has any shared amenities or property features, such as
            bicycle storage or air conditioning. You can also enter any features that make your property unique.
          </Paragraph>
          <Paragraph>
            Also, renters with cars like to know if parking is available, and may need to budget for any parking fees.
            Including information in your ad about your rental’s parking availability will save time for both you and
            the renter.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.APPLICATIONS]: {
    heading: 'Receive Zillow applications',
    body: (
      <>
        <Paragraph css={{ marginBottom: '400' }}>
          Enable Zillow applications so renters can apply directly from your listing. It’s free for you, and you’ll
          automatically receive these benefits:
        </Paragraph>
        <List.Root>
          <List.Item css={{ paddingBlock: '0' }}>Access to all applications &amp; documents in one place</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Credit report</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Background check</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Income &amp; employment history</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Income documents (if provided)</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Message with renters directly through Zillow Rental Manager</List.Item>
        </List.Root>
        <Paragraph textStyle="body-bold" css={{ marginTop: '800' }}>
          Why renters love using Zillow applications
        </Paragraph>
        <Paragraph>
          They can save time &amp; money by paying a single application fee to apply to multiple properties for 30 days.
        </Paragraph>
      </>
    ),
    premium: {
      heading: 'Receive Zillow applications',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            Enable Zillow applications so renters can apply directly from your listing. It’s free for you, and you’ll
            automatically receive these benefits:
          </Paragraph>
          <List.Root>
            <List.Item css={{ paddingBlock: '0' }}>Access to all applications &amp; documents in one place</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Credit report</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Background check</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Income &amp; employment history</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Income documents (if provided)</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              Message with renters directly through Zillow Rental Manager
            </List.Item>
          </List.Root>
          <Paragraph textStyle="body-bold" css={{ marginTop: '800' }}>
            Why renters love using Zillow applications
          </Paragraph>
          <Paragraph>
            They can save time &amp; money by paying a single application fee to apply to multiple properties for 30
            days.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.CONTACT]: {
    heading: 'Share your contact information',
    body: <Paragraph>Let renters know who is leasing the property.</Paragraph>,
  },
  [RECS.DATE_AVAILABLE]: {
    heading: 'Share when the rental is available',
    body: <Paragraph>Renters like to know when the property is available to move in.</Paragraph>,
  },
  // not actually a recommendation from the API but need to accomodate r4r
  [RECS.ROOM_DESCRIPTION]: {
    heading: 'Describe the room and shared space',
    body: (
      <>
        <Paragraph css={{ marginBottom: '200' }}>
          Start with the room’s best features, like a walk-in closet or private entrance.
        </Paragraph>
        <Paragraph css={{ marginBottom: '200' }}>
          Include key details about the room. What level is it on? Does it have an attached bathroom? What type of
          flooring does it have? Help renters imagine themselves in the space.
        </Paragraph>
        <Paragraph css={{ marginBottom: '200' }}>
          Avoid generic words (like “nice” and “beautiful”). Instead, use descriptive words: “spacious,” “airy,” “newly
          renovated,” etc.
        </Paragraph>
      </>
    ),
  },
  [RECS.DESCRIPTION]: {
    heading: 'Describe the property',
    body: (
      <>
        <Paragraph css={{ marginBottom: '200' }}>
          Write at least 300 characters describing the upgrades and desirable features that will attract renters to your
          property. We recommend 300-900 characters.
        </Paragraph>
        <Paragraph>Tips for property descriptions:</Paragraph>
        <List.Root>
          <List.Item css={{ paddingBlock: '0' }}>
            Market the property’s proximity to transit, dining, shopping, and other local attractions
          </List.Item>
          <List.Item css={{ paddingBlock: '0' }}>
            Mention upgrades, attractive amenities, and other appealing details
          </List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Indicate whether you live on-site</List.Item>
        </List.Root>
      </>
    ),
    premium: {
      heading: 'Elaborate on your description',
      body: (
        <>
          <Paragraph css={{ marginBottom: '200' }}>
            When renters are shopping for a new place to call home, they’re comparing listings to their “must-have” and
            “nice-to-have” features. Be sure to cover key information about your property.
          </Paragraph>
          <Paragraph textStyle="body-bold">Include the square footage</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Help renters visualize the space by mentioning the square footage. They may have a specific minimum size in
            mind, or they can compare your property’s square footage to their current space.
          </Paragraph>
          <Paragraph textStyle="body-bold">Highlight any recent upgrades and desirable home features</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Features like storage, flooring material, and energy-efficient upgrades will help set your listing apart
            from the rest when you advertise your rental.
          </Paragraph>
          <Paragraph>Some unique features to include, if applicable:</Paragraph>
          <List.Root>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Flooring: </Text>Is it hardwood? Laminate? Concrete?
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Natural light and windows: </Text>Having lots of windows can make a big
              difference to renters, especially if the house is smaller. Call out any oversized and floor-to-ceiling
              windows.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Storage and closets: </Text>Make sure you let prospective renters know about
              any large closets or storage space, especially if your property has a walk-in closet or pantry.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Appliances: </Text>Top-priority amenities often include air conditioning, a
              dishwasher, and in-unit laundry. Let the renter know if the kitchen has desirable features like stainless
              steel appliances or a double oven.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Smart home features: </Text>Wireless doorbells, smart thermostats, and other
              electronics can add value for many prospective renters.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Energy efficiency: </Text>Energy-efficient features are important to some
              renters.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Utilities: </Text>List what utilities are included (if any), as well as
              what&apos;s available (cable/satellite TV, internet, etc.).
            </List.Item>
          </List.Root>
          <Paragraph textStyle="body-bold" css={{ marginTop: '400' }}>
            Mention any restrictions
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Pet owners will pass over listings where pets are non-negotiable. Also include any other major rental
            restrictions, like HOA regulations.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.HIDE_PHONE]: {
    heading: 'Allow renters to contact you by phone',
    body: (
      <Paragraph>
        Connecting over the phone can be a quick way for you and the prospective renter to get to know each other,
        address any questions, and set up a tour. When you allow phone leads, your actual phone number won&apos;t
        display in the listing — the renter will receive an alias phone number that will connect them to you. This helps
        protect your privacy, reduces spam calls, and helps you organize contacts in your Zillow Rental Manager inbox.
      </Paragraph>
    ),
    premium: {
      heading: 'Allow renters to contact you by phone',
      body: (
        <Paragraph>
          On premium listings, renters can contact you by phone. Your actual phone number won&apos;t display in the
          listing — the renter will receive an alias number that connects them to you. This protects your privacy,
          reduces spam calls, and keeps contacts organized in your Zillow Rental Manager inbox.
        </Paragraph>
      ),
    },
  },
  [RECS.HIDE_STREET_ADDRESS]: {
    heading: 'Show the property address',
    body: (
      <Paragraph css={{ marginBottom: '400' }}>
        You can hide the address if privacy is a concern, but the listing may receive fewer views and contacts than
        listings that show the property address.
      </Paragraph>
    ),
    premium: {
      heading: 'Show the property address',
      body: (
        <Paragraph css={{ marginBottom: '400' }}>
          Few renters will inquire about a property without knowing where it is. Providing the address improves your
          listing’s performance by allowing renters to determine if the property is an area with easy access to shopping
          and recreation, acceptable schools, and public transportation.
        </Paragraph>
      ),
    },
  },
  [RECS.LAUNDRY]: {
    heading: 'Describe the laundry situation',
    body: <Paragraph>Add whether the laundry is in-unit or shared.</Paragraph>,
  },
  [RECS.LEASE_TERMS]: {
    heading: 'Share the lease terms',
    body: (
      <>
        <Paragraph css={{ marginBottom: '400' }}>
          Share details that can be deal breakers – or deal makers – for renters. Be upfront about fees and utility
          costs. Also include your policies about pets, smoking, and parking.
        </Paragraph>
        <Paragraph>Tips for lease terms:</Paragraph>
        <List.Root>
          <List.Item css={{ paddingBlock: '0' }}>Be upfront about your rental policies</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>Clearly list lease duration and required fees</List.Item>
          <List.Item css={{ paddingBlock: '0' }}>
            Include any relevant information about the tenant’s maintenance responsibilities
          </List.Item>
        </List.Root>
      </>
    ),
    premium: {
      heading: 'Specify your lease terms',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            Lease terms can let renters know the length of the lease and important details like your policies on
            smoking, pets, and utilities.
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>Be sure to provide the following information:</Paragraph>
          <Paragraph textStyle="body-bold">The “rental term” of the lease</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Specify the length of time the lease will be in effect — for example, it could be month-to-month with a
            specific end date or a year long with an automatic lease renewal. Be sure to check what is permitted by
            local laws.
          </Paragraph>
          <Paragraph textStyle="body-bold">Limits on occupancy</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Communicate to potential tenants any limits on the number of people who can live in the unit. You can also
            share if there are any restrictions on overnight stays.
          </Paragraph>
          <Paragraph textStyle="body-bold">Subletting exclusions</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            It can be helpful for some renters to know upfront if you have any limits on or prohibitions against
            subletting. Be sure to check what is permitted by local laws.
          </Paragraph>
          <Paragraph textStyle="body-bold">List of utilities</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Communicate what, if any, utilities such as water and garbage are included in the rent.
          </Paragraph>
          <Paragraph textStyle="body-bold">Details of your pet policy</Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            In addition to specifying the kinds of pets accepted at your property, which will help renters narrow their
            search, you should also give more information in the lease terms. Here are some points to include:
          </Paragraph>
          <List.Root>
            <List.Item css={{ paddingBlock: '0' }}>
              The number and types of pets allowed, like “two cats,” “one dog,” or “one dog under 50 pounds.”
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Any breed, vaccination, or licensing requirements.</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              Whether you require a pet screening before approving any applicants and signing a lease.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>Pet deposit or fee.</List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              Ways the property is (or is not) pet-friendly, like a fenced-in yard, durable flooring, or special potty
              areas.
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              Renters insurance requirements, if you expect tenants to purchase renters insurance that specifically
              covers dog bites and other injuries.
            </List.Item>
          </List.Root>
        </>
      ),
    },
  },
  [RECS.PHOTOS]: {
    heading: 'Add at least 5 photos',
    body: <Paragraph>Photos help renters imagine living in your space.</Paragraph>,
    premium: {
      heading: 'Add more photos',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            When creating rental listings, pictures are essential to help prospective renters visualize the space. Use
            these tips to help maximize your rental’s appeal:
          </Paragraph>
          <List.Root css={{ marginBottom: '400' }}>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Include a minimum of 5 photos.&nbsp;</Text>
              <Text>Renters are less likely to inquire about a listing with only a few images.</Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Take a photo of every room.&nbsp;</Text>
              <Text>Especially important are photos of the kitchen, bedrooms, living room and bathrooms.</Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Show exterior spaces.&nbsp;</Text>
              <Text>
                Is there a patio or deck, or maybe a large backyard? You can also highlight any nice views, landscaping,
                or the driveway or parking area.
              </Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Open blinds and curtains.&nbsp;</Text>
              <Text>This can maximize natural light and help make the space look more inviting.</Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Avoid mirrors.&nbsp;</Text>
              <Text>
                They can lead to a confusing view or make the space appear smaller. Also, make sure you&apos;re not in
                any photos.
              </Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Keep the camera steady.&nbsp;</Text>
              <Text>A tripod or other steady camera rest will ensure your photos are more clear.</Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Post large, high-resolution photos.&nbsp;</Text>
              <Text>
                Make sure your photo size is at least 300 x 500 pixels. A photo resolution of 600 x 800 pixels is best.
              </Text>
            </List.Item>
            <List.Item css={{ paddingBlock: '0' }}>
              <Text textStyle="body-bold">Shoot at an angle.&nbsp;</Text>
              <Text>This can add dimension and visual interest to the photos.</Text>
            </List.Item>
          </List.Root>
          <Paragraph css={{ marginBottom: '400' }}>
            What if the home is occupied? At a minimum, give your tenants 24 hours’ notice, or the amount of notice
            required by your local rental housing laws. Politely ask them to make sure the space is clean — you can even
            incentivize this by discounting that month’s rent or giving them a small gift.
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            If your tenant has already moved out, staging is key. Staging the home with furnishings will help give
            renters an idea of how much space they’ll have. You can even make rooms feel larger with effective staging.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.PRICE]: {
    heading: 'Add your pricing',
    body: <Paragraph>Pricing is essential for renters to know whether they can afford your property.</Paragraph>,
  },
  [RECS.SCHEDULE]: {
    heading: 'Share your tour availability ',
    body: <Paragraph>Let renters know when you&apos;re available to offer tours.</Paragraph>,
    premium: {
      heading: 'Supply your tour availability',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            Seventy-one percent of renters say they toured up to four homes in person, according to{' '}
            <Anchor
              href="https://www.zillow.com/research/renters-consumer-housing-trends-report-2022-31265/"
              target="_blank"
            >
              Zillow’s 2022 Consumer Housing Trends Report
            </Anchor>
            .
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Timing is important for busy potential renters, with weekend tours in high demand. Offering evening tours is
            important, too: It’s when many renters have free time to tour potential rentals.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.SECURITY_DEPOSIT]: {
    premium: {
      heading: 'Provide your security deposit amount',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            Make sure prospective renters know whether they can afford your property by clearly stating the security
            deposit. Security deposits are set by the landlord, so they often vary by property. When setting your
            security deposit, keep in mind that many states limit how much a landlord can charge for a security deposit
            on an apartment or rental house.
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            If you’re unsure about the particular security deposit regulations in your city and state, check with your
            local housing agency or consult an attorney.
          </Paragraph>
        </>
      ),
    },
    heading: 'Provide your security deposit amount',
    body: (
      <>
        <Paragraph css={{ marginBottom: '400' }}>
          Make sure prospective renters know whether they can afford your property by clearly stating the security
          deposit. Security deposits are set by the landlord, so they often vary by property. When setting your security
          deposit, keep in mind that many states limit how much a landlord can charge for a security deposit on an
          apartment or rental house.
        </Paragraph>
        <Paragraph css={{ marginBottom: '400' }}>
          If you’re unsure about the particular security deposit regulations in your city and state, check with your
          local housing agency or consult an attorney.
        </Paragraph>
      </>
    ),
  },
  [RECS.VIRTUAL_TOUR]: {
    heading: 'Add a 3D tour',
    body: (
      <>
        <Paragraph css={{ marginBottom: '400' }}>
          In addition to posting photos, you can take your rental ad listings to the next level with Zillow 3D Home
          virtual tours. The rich media features capture the layout of the home, helping renters picture themselves in
          the space to see if it meets their needs.
        </Paragraph>
        <Paragraph>
          Read our 3D Home Guide to learn how to use this feature — it’s easier than you might think! You can also add a
          3D tour from an external 3D tour provider.
        </Paragraph>
      </>
    ),
    premium: {
      heading: 'Add a 3D tour',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            In addition to posting photos, you can take your rental ad listings to the next level with{' '}
            <Anchor target="_blank" href="https://www.zillow.com/z/3d-home/">
              Zillow 3D Home
            </Anchor>{' '}
            virtual tours. The rich media features capture the layout of the home, helping renters picture themselves in
            the space to see if it meets their needs.
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Sixty-four percent of renters believe 3D tours help them get a better feel for a property than photos, and
            56% wish more listings included 3D tours, according to{' '}
            <Anchor
              href="https://wp-tid.zillowstatic.com/18/Zillow-Rentals_CHTR-MiniBook-2021-full-2eba65.pdf"
              target="_blank"
            >
              Zillow’s Consumer Housing Trends Report
            </Anchor>
            .
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Read our{' '}
            <Anchor href="https://www.zillow.com/z/3d-home/guide" target="_blank">
              3D Home Guide
            </Anchor>{' '}
            to learn how to use this feature — it’s easier than you might think!
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            You can also add a 3D tour from an external 3D tour provider.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.VIDEO]: {
    heading: 'Add videos',
  },
  [RECS.PETS_NOT_ALLOWED]: {
    heading: 'Share your pet policy',
    body: (
      <Paragraph css={{ marginBottom: '400' }}>
        Pets are part of many renters&apos; households: 38% have at least one dog, 29% have at least one cat, and 13%
        have another kind of pet, according to the{' '}
        <Anchor
          href="https://www.zillow.com/research/renters-consumer-housing-trends-report-2022-31265/"
          target="_blank"
        >
          2022 Zillow Consumer Housing Trends Report
        </Anchor>
        .
      </Paragraph>
    ),
    premium: {
      heading: 'Consider allowing pets',
      body: (
        <>
          <Paragraph css={{ marginBottom: '400' }}>
            A clear pet policy in your listing will save time for both you and the renter. To set expectations for
            renters and help them narrow their search, select the types of pets you allow.
          </Paragraph>
          <Paragraph css={{ marginBottom: '800' }}>
            Provide the details of your pet policy in the lease terms section of your listing.
          </Paragraph>
        </>
      ),
    },
  },
  [RECS.SQUARE_FOOTAGE]: {
    heading: 'Add your square footage',
    body: (
      <Paragraph css={{ marginBottom: '400' }}>
        Providing square footage gives renters the details they need to visualize your property.
      </Paragraph>
    ),
    premium: {
      heading: 'Add your property’s square footage',
      body: (
        <Paragraph css={{ marginBottom: '400' }}>
          Help renters visualize your space by including the square footage. They may have a specific minimum size in
          mind, or want to compare your property’s size to their current space and other rentals they’re considering.
        </Paragraph>
      ),
    },
  },
  [RECS.WEBSITE]: {
    heading: 'Add your property’s website',
    body: <Paragraph>Send traffic to your website so interested renters can learn more.</Paragraph>,
  },
  [RECS.BUSINESS_HOURS]: {
    heading: 'Add your business hours',
    body: <Paragraph>Let renters know when the leasing office is open.</Paragraph>,
  },
  [RECS.FLOOR_PLAN_LAYOUT]: {
    heading: 'Add a floor plan layout',
    body: <Paragraph>Help renters envision the layout of their potential home</Paragraph>,
  },
  [RECS.FLOOR_PLAN_PHOTOS]: {
    heading: 'Add floor plan photos',
    body: <Paragraph>Help renters visualize specific floor plans.</Paragraph>,
  },
  [RECS.UNIT_AVAILABILITY]: {
    heading: 'Add unit-level availability',
    body: <Paragraph>Let renters know details on specific available units.</Paragraph>,
  },
  [RECS.SPECIAL_OFFER]: {
    heading: 'Add a special offer',
    body: <Paragraph>Highlight any special offers that are currently available.</Paragraph>,
  },
  [RECS.LEASE_LENGTH]: {
    heading: 'Share your lease length',
    body: <Paragraph>Providing lease duration helps renters understand their rental commitment.</Paragraph>,
  },
  [RECS.PARKING]: {
    heading: 'Share your parking options',
    body: (
      <Paragraph>
        Renters with cars want to know if your property has parking available. Add what parking options are available
        and share any relevant fees.
      </Paragraph>
    ),
  },
  [RECS.ONE_TIME_FEE]: {
    heading: 'Add a required one-time fee',
    body: <Paragraph>Let renters know about any one-time fees to help them understand total cost.</Paragraph>,
  },
  [RECS.MONTHLY_FEE]: {
    heading: 'Add a required monthly fee',
    body: <Paragraph>Let renters know about any monthly fees to help them understand total cost.</Paragraph>,
  },
} as const;

export {
  RECS,
  REVIEW_PAGE_SECTION_RECS,
  COMPLETION_STATUS,
  RecsLearnMoreConstants,
  RECS_ARRAY_ORDER,
  GET_LISTING_RECS,
  RECS_TO_MF_SECTION_MAP,
  MF_SECTION_TO_INDEX_MAP,
};
